/**
 * Wave 78 F1 · SUB 1 — MapaInterativo.
 *
 * Substitui o porthole sepia + iframe legado (`NinhoVerdeMap.tsx`) por um
 * mapa Google Maps JS SDK REAL: zoom in/out, pan, mudança de camada
 * (satellite/hybrid/roadmap), botão street view explícito.
 *
 * Estado atual (antes desta wave):
 *   • Iframe legado `maps.google.com/...?output=embed` com chave NENHUMA
 *   • `pointer-events: none` — buyer não interage de jeito nenhum
 *   • Filtro CSS sepia + clip-path porthole — bonito mas estático
 *   • GPS hardcoded `-23.237837, -48.082789` em NinhoVerdeMap.tsx:47
 *   • Sem street view, sem mudança de camada, sem distância calculada
 *
 * Princípio (PRD §5.1):
 *   • 1 mapa real, controles habilitados
 *   • Layout editorial: hero copy em cima, mapa full-bleed no meio, pills
 *     de contexto embaixo, CTA "Ver no Google Maps" como saída
 *   • Mobile-first: 375px funciona; mapa preenche 56-60vh
 *
 * Estados (OPERATING-MANUAL §UI invariants):
 *   • Loading: dark shimmer dentro do containerRef (handled by InteractiveMapEmbed)
 *   • Sem lat/lng do projeto: empty state "Localização será publicada em breve"
 *   • SDK falhou: fallback CTA "Abrir no Google Maps" (handled by InteractiveMapEmbed)
 *
 * Pain points endereçados:
 *   ✓ Mapa interativo de verdade (zoom, pan, layers)
 *   ✓ Street view exposto como CTA (D6 JP = sim)
 *   ✓ GPS vem do D1 (não hardcoded), via `data.geo` populado pelo
 *     backend (Wave 78 SUB 1 service change)
 *   ✓ Editorial polish mantido: dark bg, accent orange, tipografia
 *     font-display tracking-tighter consistente com Hero/Approach/etc
 */

import type { Geo, PublicShowcase } from '../lib/api';
import { InteractiveMapEmbed } from './maps/InteractiveMapEmbed';

interface Props {
  data: PublicShowcase;
}

export function MapaInterativo({ data }: Props) {
  const geo: Geo | null = data.geo ?? null;
  const subtitle = buildSubtitle(data);
  const externalHref = buildExternalHref(data, geo);

  return (
    <section
      className="relative border-t border-line bg-bg py-24 sm:py-32"
      aria-label="Localização"
    >
      <div className="mx-auto max-w-6xl px-6 sm:px-12 lg:px-20">
        {/* Editorial header — same rhythm as other scenes */}
        <div className="mb-12 sm:mb-16">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Endereço
          </p>
          <h2 className="mt-5 max-w-[20ch] font-display text-4xl font-light leading-[1.05] tracking-tighter text-ink sm:text-5xl">
            Onde está.
          </h2>
          {subtitle && (
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-muted sm:text-base">
              {subtitle}
            </p>
          )}
        </div>

        {/* Mapa real — aspect ratio responsivo */}
        <div className="relative overflow-hidden rounded-sm border border-line bg-bg-card">
          <div className="aspect-[4/5] w-full sm:aspect-[16/9]">
            {geo ? (
              <InteractiveMapEmbed
                lat={geo.lat}
                lng={geo.lng}
                zoom={16}
                mapType="satellite"
                markerLabel={subtitle ?? 'Localização do projeto'}
                externalHref={externalHref}
                showStreetViewButton
              />
            ) : (
              <EmptyGeo />
            )}
          </div>
        </div>

        {/* Bottom pills — context layer. Genérico: enquanto o PRD não
            popular condo.access_via/condo.lagos_count, deixamos copy
            editorial estável. Quando project_specs preencher essas
            chaves, basta substituir os valores hardcoded por
            specValue() lookups (Memorial Flipbook já faz isso). */}
        <div className="mt-10 grid grid-cols-2 gap-6 sm:mt-12 sm:grid-cols-4">
          <InfoPill
            label="Cidade"
            value={
              data.city && data.state
                ? `${data.city}/${data.state}`
                : data.neighborhood ?? '—'
            }
          />
          <InfoPill label="Acesso" value="Rodovia SP-280" />
          <InfoPill label="Mapa" value="Satélite navegável" />
          <InfoPill label="Visita" value="Street view ativo" />
        </div>

        {/* CTA final — sai pro Google Maps app */}
        <div className="mt-10 flex justify-center sm:mt-14">
          <a
            href={externalHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-accent px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-accent/90"
          >
            Ver no Google Maps
          </a>
        </div>
      </div>
    </section>
  );
}

function buildSubtitle(d: PublicShowcase): string | null {
  // Prefer the curated hero subheadline if it includes location strings,
  // otherwise compose from city/state/neighborhood.
  const parts: string[] = [];
  if (d.neighborhood) parts.push(d.neighborhood);
  if (d.city) parts.push(d.city);
  if (d.state) parts.push(d.state);
  if (parts.length === 0) return d.heroSubheadline ?? null;
  return parts.join(' · ');
}

function buildExternalHref(d: PublicShowcase, geo: Geo | null): string {
  if (geo) {
    return `https://www.google.com/maps/search/?api=1&query=${geo.lat},${geo.lng}`;
  }
  const q = [d.neighborhood, d.city, d.state].filter(Boolean).join(' ').trim();
  if (q.length > 0) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  }
  return 'https://www.google.com/maps';
}

function InfoPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <p className="text-[9px] font-medium uppercase tracking-[0.3em] text-accent">
        {label}
      </p>
      <p className="text-sm font-light leading-snug text-ink">{value}</p>
    </div>
  );
}

function EmptyGeo() {
  // No lat/lng yet → soft empty state. Never iframe-fallback (which is
  // what we just replaced). The PM hasn't run the geocoding hook on this
  // project; the data will populate once the address resolves.
  return (
    <div className="flex h-full w-full items-center justify-center bg-bg-card">
      <div className="max-w-md px-6 py-12 text-center">
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
          Localização
        </p>
        <p className="mt-5 font-display text-2xl font-light leading-snug tracking-tight text-ink-muted">
          A localização exata será publicada em breve.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-ink-subtle">
          O projeto está sendo cadastrado no sistema de geolocalização.
        </p>
      </div>
    </div>
  );
}
